import React from "react";
import {
  Box,
  List,
  Skeleton,
  Theme,
  useTheme,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import TimerManager from "./Timer";
import { Sortable, Timer } from "./types";
import useConfig from "../../core/useConfig";
import { useAppSelector } from "../../store/hooks";
import { useTimerContext } from "./TimerLoader";
import { dateStringForFromAndToStringFormat, isDateFromToday } from "./helper";

const useStyle = makeStyles()(({ spacing, palette }: Theme) => ({
  root: {
    padding: "0px",
    margin: "0px",
  },
  listContainerDrawer: {
    padding: spacing(1),
    position: "absolute",
    height: "fit-content",
    maxHeight: "500px",
    width: "260px",
    // left: "50%",
    // transform: "translate(-50%, 0%)",
    right: "0",
    backgroundColor: palette.background.paper,
    borderRadius: spacing(1),
    zIndex: 200,
    opacity: 0,
    transition: "4s all ease-in-out",
    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    cursor: "default",
  },
  listContainerDrawerFade: {
    opacity: 1,
  },
  overlay: {
    position: "fixed",
    top: "0px",
    left: "0px",
    height: "100vh",
    width: "100vw",
    zIndex: 100,
  },
  listHeaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  listFilters: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  emptyTimer: {
    fontSize: "25px",
    color: palette.text.primary,
  },
  listTimerItemsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing(2),
    padding: spacing(1),
    scroll: "hidden",
    overflowY: "auto",
    height: "70%",
    transition: "0.4 all ease-in-out",
    cursor: "default",
  },
  listTimerItem: {
    display: "flex",
    alignItem: "center",
    justifyContent: "center",
    flexDirection: "column",
    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
    borderRadius: spacing(1),
    padding: spacing(1),
    gap: spacing(0.3),
  },
  listTimerItemHeader: {
    display: "flex",
    alignItem: "center",
    width: "100%",
    justifyContent: "space-between",
  },
  listTimerItemHeaderSeperator: {
    display: "flex",
    alignItems: "center",
    gap: spacing(1),
  },
  timerManagerContainer: {
    display: "flex",
    alignItem: "center",
    justifyContent: "right",
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    gap: spacing(1),
  },

  footer: {
    width: "100%",
    display: "flex",
    justifyContent: "right",
    alignItems: "center",
    padding: spacing(1),
  },
}));

const ListItemSkeleton = ({}: { length: number }) => {
  const { classes } = useStyle();
  const theme = useTheme();
  return (
    <Box sx={{ height: "70%", display: "flex", flexDirection: "column", gap: theme?.spacing(2) }}>
      {[0, 0, 0, 0, 0, 0].map((_, index) => (
        <Box key={index} style={{ width: "100%", height: "fit-content" }}>
          <Skeleton variant="rounded" width={"100%"} height={100} />
        </Box>
      ))}
    </Box>
  );
};

interface Props {
  timers?: Timer[];
  setSorted?: (sort: Sortable) => void;
  isDrawer?: boolean;
  handleCloseListDrawer?: () => void;
  closeDrawer?: any;
  minimization?: boolean;
  loading?: boolean;
}

const TimerList: React.FC<Props> = ({ isDrawer, handleCloseListDrawer, minimization, loading }) => {
  const { timezone } = useConfig();
  const { data: timers } = useAppSelector((state) => state.timer.timers);
  const { timer: storeTimer, } = useAppSelector((state) => state.timer);
  const { handleLocalValues, localValues, handleSetPausedTimer } = useTimerContext();
  const { classes } = useStyle();
  const theme = useTheme();
  const dummyRef = React.useRef();
  const drawerRef = React.useRef<any>(null);
  const handleGetDate = (e: any) => {
    const modifiedStrings = dateStringForFromAndToStringFormat(e?.target?.value, timezone!);
    handleLocalValues && handleLocalValues({ key: "fromDate", value: modifiedStrings?.fromDate });
    handleLocalValues && handleLocalValues({ key: "toDate", value: modifiedStrings?.toDate });
    // handleLocalValues && handleLocalValues({ key: "visibleDate", value: modifiedStrings?.fromDate })
  };
  
  return (
    <List dense sx={{width:'100%'}}>
      {timers?.map((timer) => (
        <TimerManager
              uniqueKey={timer?.id}
              durationInSeconds={timer?.durationInSeconds ? timer?.durationInSeconds : 0}
              isClickAble={true}
              isPlayButton={true}
              isTimerRunning={(() => {
                return storeTimer?.id === timer?.id ? true : false;
              })()}
              isChild={true}
              isCurrentDateTimer={isDateFromToday(timer?.createdOn!)}
            />
      ))}
    </List>
    // <>
    //     {isDrawer && (
    //         <Box
    //             className={`${classes.listContainerDrawer} ${
    //                 isDrawer ? classes.listContainerDrawerFade : ''
    //             }`}
    //             onClick={(e) => {
    //                 e.stopPropagation()
    //             }}
    //         >
    //             <header className={classes?.listHeaderContainer}>
    //                 <Typography color={'primary'} variant="h5">
    //                     Timers
    //                 </Typography>
    //                 {minimization && (
    //                     <Box sx={{ maxWidth: '200px' }}>
    //                         <TimerManager isChild={false} isPlayButton={true} position="body" />
    //                     </Box>
    //                 )}
    //                 <IconButton onClick={handleCloseListDrawer}>
    //                     <CloseOutlined />
    //                 </IconButton>
    //             </header>
    //             <Box className={classes.listFilters}>
    //                 <DatePicker
    //                     defaultValue={moment(localValues?.fromDate)}
    //                     value={moment(localValues?.fromDate)}
    //                     onChange={handleGetDate}
    //                     name="date"
    //                     sx={{ width: '40%' }}
    //                 />

    //                 {/* <span className={classes.emptyTimer}>--:--:--</span> */}
    //             </Box>
    //             <Box className={classes?.listTimerItemsContainer}>
    //                 {loading ? (
    //                     <ListItemSkeleton length={10} />
    //                 ) : timers?.length === 0 ? (
    //                     <Box
    //                         sx={{
    //                             display: 'flex',
    //                             alignItems: 'center',
    //                             justifyContent: 'center',
    //                             height: '100%',
    //                             width: '100%',
    //                             padding: theme?.spacing(1),
    //                         }}
    //                     >
    //                         No Data Found
    //                         {/* <Layout>
    //                             <TimerEmptyState
    //                                 message={
    //                                     <Typography color={'primary'} variant="subtitle2">
    //                                         <Trans id="timer-no-timer-available">
    //                                             No Data Found!
    //                                         </Trans>
    //                                     </Typography>
    //                                 }
    //                             />
    //                         </Layout> */}
    //                     </Box>
    //                 ) : (
    //                     timers?.map((timer: Timer) => {
    //                         return (
    //                             <Box key={timer?.id} className={classes?.listTimerItem}>
    //                                 <Box
    //                                     sx={{ borderBottom: '1px dashed gray' }}
    //                                     className={classes?.listTimerItemHeader}
    //                                 >
    //                                     {/* <Typography variant={'subtitle1'}>{timer?.matter?.title ? timer?.matter?.title : "--"}</Typography> */}
    //                                     <Box
    //                                         className={classes.listTimerItemHeaderSeperator}
    //                                         sx={{ display: 'flex', alignItems: 'center' }}
    //                                     >
    //                                         <Typography
    //                                             color={'primary'}
    //                                             sx={{ display: 'flex', alignItems: 'center' }}
    //                                             variant={'subtitle1'}
    //                                         >
    //                                             Matter :
    //                                         </Typography>
    //                                         <Tooltip
    //                                             title={
    //                                                 timer?.matter?.title
    //                                                     ? timer?.matter?.title
    //                                                     : '--'
    //                                             }
    //                                         >
    //                                             <Typography
    //                                                 color={'primary'}
    //                                                 sx={{
    //                                                     display: 'flex',
    //                                                     alignItems: 'center',
    //                                                 }}
    //                                                 variant={'subtitle2'}
    //                                             >
    //                                                 {timer?.matter?.title
    //                                                     // ? truncateText(timer?.matter?.title, 30)
    //                                                     ? timer?.matter?.title
    //                                                     : '--'}
    //                                             </Typography>
    //                                         </Tooltip>
    //                                     </Box>
    //                                     <Box
    //                                         sx={{
    //                                             cursor: timer?.isTimerRunning
    //                                                 ? 'not-allowed'
    //                                                 : 'pointer',
    //                                         }}
    //                                         className={classes?.actionContainer}
    //                                     >
    //                                         <IconButton
    //                                             disabled={timer?.isTimerRunning ? true : false}
    //                                             onClick={() => {
    //                                                 handleSetPausedTimer &&
    //                                                     handleSetPausedTimer({
    //                                                         id: timer?.id,
    //                                                         durationInSeconds:
    //                                                             timer?.durationInSeconds!,
    //                                                         isPaused: true,
    //                                                     })
    //                                             }}
    //                                         >
    //                                             <EditOutlined fontSize="small" />
    //                                         </IconButton>
    //                                     </Box>
    //                                 </Box>
    //                                 <Box className={classes.timerManagerContainer}>
    // <TimerManager
    //     uniqueKey={timer?.id}
    //     durationInSeconds={
    //         timer?.durationInSeconds
    //             ? timer?.durationInSeconds
    //             : 0
    //     }
    //     isClickAble={true}
    //     position="body"
    //     isPlayButton={true}
    //     isTimerRunning={(() => {
    //         return storeTimer?.id === timer?.id
    //             ? true
    //             : false
    //     })()}
    //     isChild={true}
    //     isCurrentDateTimer={isDateFromToday(
    //         timer?.createdOn!
    //     )}
    // />
    //                                 </Box>
    //                             </Box>
    //                         )
    //                     })
    //                 )}
    //             </Box>
    //         </Box>
    //     )}
    //     {isDrawer && <Box onClick={handleCloseListDrawer} className={classes.overlay}></Box>}
    // </>
  );
};

export default TimerList;
